import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Image, File, Download, Loader2, Upload } from 'lucide-react';
import { Button } from './ui/button';

export default function DocumentList() {
    const { t } = useTranslation();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const [targetCategory, setTargetCategory] = useState(null);

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        try {
            // Mock documents for demo
            const mockDocuments = [
                {
                    name: "Safety Manual.pdf",
                    type: "pdf",
                    category: "01_Policy",
                    path: "safety-manual.pdf"
                },
                {
                    name: "Equipment Guide.docx",
                    type: "word",
                    category: "03_Teknisk_Drift",
                    path: "equipment-guide.docx"
                },
                {
                    name: "Emergency Procedures.pdf",
                    type: "pdf",
                    category: "02_Sikkerhet",
                    path: "emergency-procedures.pdf"
                },
                {
                    name: "Production Checklist.pdf",
                    type: "pdf",
                    category: "04_Produksjon",
                    path: "production-checklist.pdf"
                }
            ];
            setDocuments(mockDocuments);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const downloadFile = async (path, name) => {
        try {
            // Mock download - in real implementation would fetch from server
            alert(`Downloading: ${name}`);
        } catch (err) {
            console.error("Download error:", err);
            alert("Failed to download file.");
        }
    };

    const handleUploadClick = (category) => {
        setTargetCategory(category);
        fileInputRef.current.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file || !targetCategory) return;

        setUploading(true);
        const formData = new FormData();
        formData.append("file", file);
        formData.append("category", targetCategory);

        try {
            const res = await fetch('http://localhost:3001/api/documents/upload/', {
                method: "POST",
                body: formData,
            });

            if (!res.ok) throw new Error("Upload failed");

            // Refresh list
            await fetchDocuments();
            alert("Manual uploaded successfully!");
        } catch (err) {
            console.error(err);
            alert("Failed to upload manual.");
        } finally {
            setUploading(false);
            setTargetCategory(null);
            event.target.value = null; // Reset input
        }
    };

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
    if (error) return <div className="text-red-500 p-4">Error: {error}</div>;

    // Group by category
    const grouped = documents.reduce((acc, doc) => {
        const cat = doc.category || "Uncategorized";
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(doc);
        return acc;
    }, {});

    return (
        <div className="space-y-6 pb-20">
            <h2 className="text-xl font-bold px-1">{t('documents_library')}</h2>

            {Object.keys(grouped).length === 0 && <p className="text-gray-500 dark:text-gray-400 text-center py-10">{t('no_documents')}</p>}

            {Object.keys(grouped).map(category => (
                <div key={category} className="space-y-3">
                    <div className="flex items-center justify-between border-b pb-1">
                        <h3 className="font-semibold text-lg text-gray-700 capitalize">{category.replace('_', ' ')}</h3>
                        {category === '03_Teknisk_Drift' && (
                            <Button variant="outline" size="sm" onClick={() => handleUploadClick(category)} disabled={uploading}>
                                {uploading ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                                {t('upload_manual')}
                            </Button>
                        )}
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                        {grouped[category].map((doc, i) => (
                            <div key={i} className="bg-white p-3 rounded-lg border shadow-sm flex items-center justify-between">
                                <div className="flex items-center gap-3 overflow-hidden">
                                    <div className="p-2 bg-gray-100 rounded-md shrink-0">
                                        {doc.type === 'pdf' && <FileText className="text-red-500" size={20} />}
                                        {doc.type === 'image' && <Image className="text-blue-500" size={20} />}
                                        {doc.type === 'word' && <FileText className="text-blue-700" size={20} />}
                                        {doc.type === 'file' && <File className="text-gray-500" size={20} />}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-medium text-sm truncate">{doc.name}</p>
                                        <p className="text-xs text-gray-500 capitalize">{doc.type}</p>
                                    </div>
                                </div>
                                <Button variant="ghost" size="icon" onClick={() => downloadFile(doc.path, doc.name)}>
                                    <Download size={18} className="text-gray-500" />
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept=".pdf,.doc,.docx" // Restrict mainly to docs
                onChange={handleFileChange}
            />
        </div>
    );
}